import React, { useState } from 'react';
import { supabase } from '../global/supabase';
import HamburgerMenu from '../global/menu';
import Toast from '../global/Toast';
import { isSelfWitnessed } from '../global/selfWitness';
import { LOCATIONS, MEDICATIONS, MEDICATION_IDS, formatMedicationName, BRANDING } from '../config';
import '../global/styles.css';

function InventoryReport({ userInfo, isAdmin, onLogout, onNavigate }) {
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [datePreset, setDatePreset] = useState('');
  const [selfWitnessedOnly, setSelfWitnessedOnly] = useState(false);
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

  const showToast = (message, type = 'success') => {
    setToast({ show: true, message, type });
  };

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <div className="container-center">
        <div className="card card-center">
          <h1 className="title">Access Denied</h1>
          <p className="subtitle">You do not have administrator privileges.</p>
          <button onClick={() => onNavigate('home')} className="btn btn-primary">
            Return to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // Date preset calculations
  const getDateRange = (preset) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let start, end;

    switch (preset) {
      case 'today':
        start = today;
        end = today;
        break;
      case 'yesterday': {
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        start = yesterday;
        end = yesterday;
        break;
      }
      case 'last7':
        start = new Date(today);
        start.setDate(start.getDate() - 6);
        end = today;
        break;
      case 'last30':
        start = new Date(today);
        start.setDate(start.getDate() - 29);
        end = today;
        break;
      case 'thisMonth':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        end = today;
        break;
      case 'lastMonth': {
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0); // last day of prev month
        break;
      }
      case 'thisQuarter': {
        const quarterStart = Math.floor(now.getMonth() / 3) * 3;
        start = new Date(now.getFullYear(), quarterStart, 1);
        end = today;
        break;
      }
      case 'thisYear':
        start = new Date(now.getFullYear(), 0, 1);
        end = today;
        break;
      case 'lastYear': {
        start = new Date(now.getFullYear() - 1, 0, 1);
        end = new Date(now.getFullYear() - 1, 11, 31);
        break;
      }
      case 'custom':
        return null; // handled by custom fields
      default:
        return null;
    }

    // Format as YYYY-MM-DD
    const fmt = (d) => d.toISOString().split('T')[0];
    return { start: fmt(start), end: fmt(end) };
  };

  const handlePresetChange = (preset) => {
    setDatePreset(preset);
    if (preset !== 'custom') {
      const range = getDateRange(preset);
      if (range) {
        setCustomStartDate(range.start);
        setCustomEndDate(range.end);
      }
    } else {
      setCustomStartDate('');
      setCustomEndDate('');
    }
  };

  // Location multi-select handlers
  const toggleLocation = (locationId) => {
    setSelectedLocations(prev =>
      prev.includes(locationId)
        ? prev.filter(id => id !== locationId)
        : [...prev, locationId]
    );
  };

  const selectAllLocations = () => {
    setSelectedLocations(LOCATIONS.map(loc => loc.id));
  };

  const clearAllLocations = () => {
    setSelectedLocations([]);
  };

  // Run report
  const runReport = async () => {
    if (selectedLocations.length === 0) {
      showToast('Please select at least one location.', 'warning');
      return;
    }
    if (!customStartDate || !customEndDate) {
      showToast('Please select a date range.', 'warning');
      return;
    }

    setLoading(true);
    setReportData([]);

    try {
      // Get selected location objects
      const selectedLocs = LOCATIONS.filter(loc => selectedLocations.includes(loc.id));

      const promises = selectedLocs.map(async (loc) => {
        const { data, error } = await supabase
          .from(loc.tableName)
          .select('*')
          .gte('created_at', customStartDate)
          .lte('created_at', customEndDate + 'T23:59:59')
          .order('created_at', { ascending: false });

        if (error) {
          console.error(`Error querying ${loc.tableName}:`, error);
          return [];
        }

        return (data || []).map(record => ({ ...record, locationName: loc.name, locationType: loc.type }));
      });

      const results = await Promise.all(promises);
      const allRecords = results.flat();

      // Format data for display
      const formattedData = allRecords.map(record => {
        // Build medication quantities
        const meds = {};
        MEDICATION_IDS.forEach(medId => {
          let qty = 0;
          if (record[medId] && parseFloat(record[medId]) > 0) {
            qty = parseFloat(record[medId]);
          } else if (record[medId + '_end'] && parseFloat(record[medId + '_end']) > 0) {
            qty = parseFloat(record[medId + '_end']);
          } else if (record[medId + '_begin'] && parseFloat(record[medId + '_begin']) > 0) {
            qty = parseFloat(record[medId + '_begin']);
          }
          meds[medId] = qty;
        });

        const narcSummary = MEDICATION_IDS
          .filter(medId => meds[medId] > 0)
          .map(medId => `(${meds[medId]}) ${formatMedicationName(medId)}`)
          .join(', ') || 'None recorded';

        return {
          location: record.locationName,
          date: record.created_at ? new Date(record.created_at).toLocaleDateString() : '',
          time: record.created_at ? new Date(record.created_at).toLocaleTimeString() : '',
          userName: record.user_name || '',
          reason: record.reason_for_change || '',
          narcSummary,
          ...meds,
          selfWitnessed: isSelfWitnessed(record),
          rawDate: record.created_at
        };
      }).filter(row => !selfWitnessedOnly || row.selfWitnessed);

      // Sort by date descending
      formattedData.sort((a, b) => new Date(b.rawDate) - new Date(a.rawDate));

      setReportData(formattedData);

      if (formattedData.length === 0) {
        showToast('No records found for the selected criteria.', 'info');
      }
    } catch (error) {
      console.error('Error running inventory report:', error);
      showToast('Error running report. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Sorting
  const handleSort = (column) => {
    let direction = 'asc';
    if (sortConfig.key === column && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key: column, direction });

    const sortedData = [...reportData].sort((a, b) => {
      if (column === 'date') {
        return direction === 'asc'
          ? new Date(a.rawDate) - new Date(b.rawDate)
          : new Date(b.rawDate) - new Date(a.rawDate);
      }
      if (a[column] < b[column]) return direction === 'asc' ? -1 : 1;
      if (a[column] > b[column]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setReportData(sortedData);
  };

  const getSortIcon = (column) => {
    if (sortConfig.key !== column) return '';
    return sortConfig.direction === 'asc' ? ' ^' : ' v';
  };

  // CSV Export
  const exportToCSV = () => {
    if (reportData.length === 0) {
      showToast('No data to export.', 'warning');
      return;
    }

    const medHeaders = MEDICATIONS.map(m => m.displayName);
    const headers = ['Location', 'Date', 'Time', 'User', 'Reason for Change', ...medHeaders];
    const csvData = [
      headers.join(','),
      ...reportData.map(row => {
        const medValues = MEDICATION_IDS.map(medId => row[medId] || 0);
        return [row.location, row.date, row.time, row.userName, row.reason, ...medValues]
          .map(field => `"${field}"`)
          .join(',');
      })
    ].join('\n');

    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `inventory_report_${BRANDING.companyName.toLowerCase().replace(/\s+/g, '_')}_${customStartDate}_to_${customEndDate}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Get friendly date range label
  const getDateRangeLabel = () => {
    if (!customStartDate || !customEndDate) return '';
    const start = new Date(customStartDate + 'T00:00:00');
    const end = new Date(customEndDate + 'T00:00:00');
    return `${start.toLocaleDateString()} to ${end.toLocaleDateString()}`;
  };

  // Summary stats
  const getSummaryStats = () => {
    if (reportData.length === 0) return null;

    const uniqueLocations = [...new Set(reportData.map(r => r.location))].length;
    const uniqueUsers = [...new Set(reportData.map(r => r.userName).filter(Boolean))].length;
    const reasonCounts = {};
    reportData.forEach(r => {
      if (r.reason) {
        reasonCounts[r.reason] = (reasonCounts[r.reason] || 0) + 1;
      }
    });

    const selfWitnessed = reportData.filter(r => r.selfWitnessed).length;

    return { uniqueLocations, uniqueUsers, reasonCounts, selfWitnessed, totalRecords: reportData.length };
  };

  const stats = getSummaryStats();

  return (
    <div className="container">
      <HamburgerMenu
        isAdmin={isAdmin}
        onLogout={onLogout}
        onNavigate={onNavigate}
      />

      <div className="report-content">
        {/* Back Button */}
        <button
          onClick={() => onNavigate('admin-dashboard')}
          className="btn btn-secondary"
          style={{ marginBottom: '20px', padding: '10px 20px' }}
        >
          Back to Admin
        </button>

        {/* Header */}
        <div className="card" style={{
          textAlign: 'center',
          background: 'var(--primary-color)',
          color: 'white',
          marginBottom: '30px'
        }}>
          <h1 style={{ color: 'white', marginBottom: '10px' }}>
            Inventory Report
          </h1>
          <p style={{ color: 'white', margin: 0 }}>
            Generate comprehensive inventory reports across one or multiple locations for any time period.
          </p>
        </div>

        {/* Location Selection */}
        <div className="card" style={{ marginBottom: '20px' }}>
          <h3 className="section-title">Select Locations</h3>
          <div style={{ display: 'flex', gap: '10px', marginBottom: '15px', flexWrap: 'wrap' }}>
            <button
              className="btn btn-secondary"
              onClick={selectAllLocations}
              style={{ padding: '6px 16px', fontSize: '13px' }}
            >
              Select All
            </button>
            <button
              className="btn btn-secondary"
              onClick={clearAllLocations}
              style={{ padding: '6px 16px', fontSize: '13px' }}
            >
              Clear All
            </button>
            <span style={{ 
              alignSelf: 'center', 
              color: 'var(--text-muted)', 
              fontSize: '13px' 
            }}>
              {selectedLocations.length} of {LOCATIONS.length} selected
            </span>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: '8px'
          }}>
            {LOCATIONS.map(loc => (
              <label
                key={loc.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '10px 12px',
                  borderRadius: '8px',
                  border: selectedLocations.includes(loc.id)
                    ? '2px solid var(--primary-color)'
                    : '2px solid var(--border-color)',
                  background: selectedLocations.includes(loc.id)
                    ? 'rgba(197, 160, 46, 0.08)'
                    : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.15s ease',
                  fontSize: '14px',
                  color: 'var(--text-color)',
                  fontWeight: selectedLocations.includes(loc.id) ? '600' : '400'
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedLocations.includes(loc.id)}
                  onChange={() => toggleLocation(loc.id)}
                  style={{ accentColor: 'var(--primary-color)' }}
                />
                {loc.name}
              </label>
            ))}
          </div>
        </div>

        {/* Date Range Selection */}
        <div className="card" style={{ marginBottom: '30px' }}>
          <h3 className="section-title">Date Range</h3>
          
          {/* Preset Buttons */}
          <div style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: '8px',
            marginBottom: '20px'
          }}>
            {[
              { value: 'today', label: 'Today' },
              { value: 'yesterday', label: 'Yesterday' },
              { value: 'last7', label: 'Last 7 Days' },
              { value: 'last30', label: 'Last 30 Days' },
              { value: 'thisMonth', label: 'This Month' },
              { value: 'lastMonth', label: 'Last Month' },
              { value: 'thisQuarter', label: 'This Quarter' },
              { value: 'thisYear', label: 'This Year' },
              { value: 'lastYear', label: 'Last Year' },
              { value: 'custom', label: 'Custom Range' },
            ].map(preset => (
              <button
                key={preset.value}
                className={datePreset === preset.value ? 'btn btn-primary' : 'btn btn-secondary'}
                onClick={() => handlePresetChange(preset.value)}
                style={{
                  padding: '8px 16px',
                  fontSize: '13px',
                  fontWeight: datePreset === preset.value ? '600' : '400'
                }}
              >
                {preset.label}
              </button>
            ))}
          </div>

          {/* Custom Date Inputs (always visible so user can see/override preset dates) */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '20px',
            alignItems: 'end'
          }}>
            <div>
              <label className="form-label">Start Date</label>
              <input
                type="date"
                className="form-input"
                value={customStartDate}
                onChange={(e) => {
                  setCustomStartDate(e.target.value);
                  if (datePreset !== 'custom') setDatePreset('custom');
                }}
              />
            </div>
            <div>
              <label className="form-label">End Date</label>
              <input
                type="date"
                className="form-input"
                value={customEndDate}
                onChange={(e) => {
                  setCustomEndDate(e.target.value);
                  if (datePreset !== 'custom') setDatePreset('custom');
                }}
              />
            </div>
            <div>
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={selfWitnessedOnly}
                  onChange={(e) => setSelfWitnessedOnly(e.target.checked)}
                />
                Self-witnessed only
              </label>
            </div>
            <div>
              <button
                className="btn btn-primary btn-full"
                onClick={runReport}
                disabled={loading}
              >
                {loading ? 'Loading...' : 'Run Report'}
              </button>
            </div>
            <div>
              <button
                className="btn btn-secondary btn-full"
                onClick={exportToCSV}
                disabled={reportData.length === 0}
              >
                Export to CSV
              </button>
            </div>
          </div>
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: 'center', margin: '20px 0' }}>
            <p>Loading report data across {selectedLocations.length} location{selectedLocations.length !== 1 ? 's' : ''}...</p>
          </div>
        )}

        {/* Summary Stats */}
        {stats && (
          <div className="card" style={{ marginBottom: '20px' }}>
            <h3 className="section-title">Report Summary</h3>
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
              gap: '15px',
              marginBottom: '15px'
            }}>
              <div style={{
                textAlign: 'center',
                padding: '15px',
                borderRadius: '8px',
                background: 'var(--background-color)',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--primary-color)' }}>
                  {stats.totalRecords}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Total Records
                </div>
              </div>
              <div style={{
                textAlign: 'center',
                padding: '15px',
                borderRadius: '8px',
                background: 'var(--background-color)',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--primary-color)' }}>
                  {stats.uniqueLocations}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Locations
                </div>
              </div>
              <div style={{
                textAlign: 'center',
                padding: '15px',
                borderRadius: '8px',
                background: 'var(--background-color)',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--primary-color)' }}>
                  {stats.uniqueUsers}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Unique Users
                </div>
              </div>
              <div style={{
                textAlign: 'center',
                padding: '15px',
                borderRadius: '8px',
                background: 'var(--background-color)',
                border: '1px solid var(--border-color)'
              }}>
                <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--primary-color)' }}>
                  {stats.selfWitnessed} of {stats.totalRecords}
                </div>
                <div style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '4px' }}>
                  Self-witnessed
                </div>
              </div>
            </div>

            {/* Reason breakdown */}
            {Object.keys(stats.reasonCounts).length > 0 && (
              <div>
                <h4 style={{ fontSize: '14px', color: 'var(--text-color)', marginBottom: '10px' }}>
                  Activity Breakdown
                </h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {Object.entries(stats.reasonCounts)
                    .sort((a, b) => b[1] - a[1])
                    .map(([reason, count]) => (
                      <span
                        key={reason}
                        style={{
                          padding: '4px 12px',
                          borderRadius: '20px',
                          background: 'var(--background-color)',
                          border: '1px solid var(--border-color)',
                          fontSize: '13px',
                          color: 'var(--text-color)'
                        }}
                      >
                        {reason}: <strong>{count}</strong>
                      </span>
                    ))
                  }
                </div>
              </div>
            )}

            {getDateRangeLabel() && (
              <p style={{ fontSize: '13px', color: 'var(--text-muted)', marginTop: '10px' }}>
                Date range: {getDateRangeLabel()}
              </p>
            )}
          </div>
        )}

        {/* Results Table */}
        {reportData.length > 0 && (
          <div className="card">
            <h3 className="section-title">
              Inventory Records ({reportData.length} records)
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%',
                borderCollapse: 'collapse',
                marginTop: '20px'
              }}>
                <thead>
                  <tr style={{ background: 'var(--background-color)' }}>
                    {[
                      { key: 'location', label: 'Location' },
                      { key: 'date', label: 'Date' },
                      { key: 'time', label: 'Time' },
                      { key: 'userName', label: 'User' },
                      { key: 'reason', label: 'Reason' },
                      { key: 'narcSummary', label: 'Medications' }
                    ].map(column => (
                      <th
                        key={column.key}
                        onClick={() => handleSort(column.key)}
                        style={{
                          padding: '12px',
                          textAlign: 'left',
                          cursor: 'pointer',
                          borderBottom: '2px solid var(--primary-color)',
                          fontWeight: '600',
                          color: 'var(--text-color)',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {column.label}{getSortIcon(column.key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {reportData.map((row, index) => (
                    <tr key={index} style={{
                      borderBottom: '1px solid var(--border-color)',
                      backgroundColor: index % 2 === 0 ? 'white' : 'var(--background-color)'
                    }}>
                      <td style={{ padding: '12px', color: 'var(--text-color)', fontWeight: '500' }}>{row.location}</td>
                      <td style={{ padding: '12px', color: 'var(--text-color)' }}>{row.date}</td>
                      <td style={{ padding: '12px', color: 'var(--text-color)' }}>{row.time}</td>
                      <td style={{ padding: '12px', color: 'var(--text-color)' }}>{row.userName}</td>
                      <td style={{ padding: '12px', color: 'var(--text-color)' }}>{row.reason}</td>
                      <td style={{ padding: '12px', color: 'var(--text-color)', fontSize: '13px', lineHeight: '1.4' }}>{row.narcSummary}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* No Results */}
        {!loading && reportData.length === 0 && selectedLocations.length > 0 && customStartDate && customEndDate && (
          <div className="card" style={{ textAlign: 'center' }}>
            <p style={{ color: 'var(--text-muted)', fontSize: '16px' }}>
              No records found for the selected criteria.
            </p>
          </div>
        )}
      </div>

      {/* Toast */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}

      <footer className="footer">
        <p className="text-small">
          Inventory Report - {BRANDING.copyright}
        </p>
      </footer>
    </div>
  );
}

export default InventoryReport;
